import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  /** Richer alt text for SEO and accessibility */
  imageAlt?: string;
  features: string[];
  onSelect: () => void;
  isSelected?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  image,
  imageAlt,
  features,
  onSelect,
  isSelected = false,
}) => {
  const { theme: customTheme } = useTheme();

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          position: 'relative',
          overflow: 'hidden',
        }}
        onClick={onSelect}
      >
        <CardMedia
          component="img"
          height="200"
          image={image}
          alt={imageAlt ?? title}
          sx={{
            objectFit: 'cover',
            filter: isSelected ? 'brightness(1.1)' : 'brightness(1)',
          }}
        />
        
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" component="div" gutterBottom fontWeight="bold">
            {title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {description}
          </Typography>

          <Box mb={2}>
            {features.map((feature, index) => (
              <Chip
                key={index}
                label={feature}
                size="small"
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: customTheme.accentColor + '20',
                  color: 'text.primary',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>

          <Button
            variant={isSelected ? "contained" : "outlined"}
            fullWidth
            sx={{
              mt: 2,
              background: isSelected 
                ? `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`
                : 'transparent',
              '&:hover': {
                background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
              },
            }}
          >
            {isSelected ? 'Selected' : 'Select Service'}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ServiceCard;

